import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import useAppStore from '@/store/app';

const SOCKET_URL = import.meta.env.VITE_CHAT_API;

interface UseSocketOptions {
	onConnect?: (socket: Socket) => void;
	onDisconnect?: (reason: Socket.DisconnectReason) => void;
	onError?: (error: Error) => void;
}

const useSocket = ({
	onConnect,
	onDisconnect,
	onError
}: UseSocketOptions = {}) => {
	const connectorData = useAppStore((state) => state.connectorData);
	const socketRef = useRef<Socket | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const connectionKey =
		connectorData.client_secret && connectorData.external_id
			? `${connectorData.client_secret}:${connectorData.external_id}`
			: null;

	useEffect(() => {
		if (!connectorData.client_secret || !connectorData.external_id) {
			socketRef.current?.disconnect();
			socketRef.current = null;
			setIsConnected(false);
			return;
		}

		const socket = io(SOCKET_URL, {
			autoConnect: true,
			transports: ['websocket'],
			query: {
				client_secret: connectorData.client_secret,
				origin: window.location.origin,
				external_id: connectorData.external_id
			}
		});

		socketRef.current = socket;

		const handleConnect = () => {
			setIsConnected(true);
			onConnect?.(socket);
		};

		const handleDisconnect = (reason: Socket.DisconnectReason) => {
			setIsConnected(false);
			onDisconnect?.(reason);
		};

		const handleConnectError = (err: Error) => {
			onError?.(err);
		};

		socket.on('connect', handleConnect);
		socket.on('disconnect', handleDisconnect);
		socket.on('connect_error', handleConnectError);

		return () => {
			socket.off('connect', handleConnect);
			socket.off('disconnect', handleDisconnect);
			socket.off('connect_error', handleConnectError);
			socket.disconnect();
			setIsConnected(false);

			if (socketRef.current === socket) {
				socketRef.current = null;
			}
		};
	}, [
		connectorData.client_secret,
		connectorData.external_id,
		onConnect,
		onDisconnect,
		onError
	]);

	const emit = useCallback(<T>(event: string, data?: T) => {
		socketRef.current?.emit(event, data);
	}, []);

	const subscribe = useCallback(
		<T>(event: string, callback: (data: T) => void) => {
			const socket = socketRef.current;

			socket?.on(event, callback);

			return () => {
				socket?.off(event, callback);
			};
		},
		[]
	);

	return {
		socket: socketRef.current,
		isConnected,
		emit,
		subscribe,
		connectionKey
	};
};

export default useSocket;
